/**
 * Event Log Page
 *
 * Admin-only page displaying monitor events in a table.
 */

import { api } from '../api/client.js';
import { apiCall } from '../api/helpers.js';
import { initAuthenticatedPage, hasPermission, hideLoading } from '../app/init.js';
import type { MonitorEvent } from '../api/types.js';

/**
 * Format event type for display: VM_STARTED -> VM Started
 */
function formatEventType(type: string): string {
  return type
    .split('_')
    .map(word => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Get CSS class for severity badge
 */
function severityClass(severity: string): string {
  switch (severity) {
    case 'error': return 'severity-error';
    case 'warn':  return 'severity-warn';
    case 'info':  return 'severity-info';
    case 'debug': return 'severity-debug';
    default:      return 'severity-debug';
  }
}

/**
 * Render events into the table body
 */
function renderEvents(events: MonitorEvent[]): void {
  const tbody = document.getElementById('events-table-body');
  if (!tbody) return;

  if (events.length === 0) {
    tbody.innerHTML = '<tr><td colspan="4" class="no-events">No events recorded.</td></tr>';
    return;
  }

  tbody.innerHTML = '';
  for (const event of events) {
    const tr = document.createElement('tr');
    const time = new Date(event.timestamp).toLocaleString();
    tr.innerHTML =
      `<td>${time}</td>` +
      `<td><span class="severity-badge ${severityClass(event.severity)}">${event.severity}</span></td>` +
      `<td>${formatEventType(event.type)}</td>` +
      `<td>${event.message}</td>`;
    tbody.appendChild(tr);
  }
}

/**
 * Initialize the events page
 */
export async function initEventsPage(): Promise<void> {
  const [user, eventsResult] = await Promise.all([
    initAuthenticatedPage(),
    apiCall(api.getMonitorEvents(), 'Failed to load events'),
  ]);

  if (!user) return;

  if (!hasPermission('admin')) {
    window.location.href = '/';
    return;
  }

  hideLoading();

  if (eventsResult) {
    renderEvents(eventsResult.events);
  }
}

/**
 * Navigate to events page
 */
export function goToEvents(): void {
  window.location.href = '/events';
}

// Export to window for onclick handlers
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).initEventsPage = initEventsPage;
  (window as unknown as Record<string, unknown>).goToEvents = goToEvents;
}
